/**
 * insights.ts - Regras puras da subaba Indicadores do Resumo da O.S. (BR-OS-055).
 *
 * Recebe os fatos brutos da RPC `get_service_order_insights` (eventos estruturados,
 * itens, checklists e remessas) e calcula durações, pontualidade, retrabalho e custo.
 * Etapa sem registro fica `null` (a tela mostra "Não registrado"), nunca valor inventado.
 *
 * RELEASE: 2026-10-18 v1.4.0 - cycleTotals: Sem agendamento = criação até Agendada, absorvendo a logística (detalhe à parte).
 * RELEASE: 2026-10-18 v1.3.0 - Grupo 'unscheduled' (Sem agendamento): Aberta/A reagendar; Agendamento só Agendada.
 * RELEASE: 2026-10-18 v1.2.0 - Grupo 'travel' (Deslocamento): withTravelSegment recorta início do deslocamento → check-in.
 * RELEASE: 2026-10-18 v1.1.0 - Regras do Gantt (grupos, barras, marcas, totais).
 * RELEASE: 2026-10-18 v1.0.0 - Versão inicial.
 */

export interface InsightEvent { type: string; old: string | null; new: string | null; at: string }
export interface InsightShipment {
  number: string; direction: string | null; status: string; freight_cost: number | null;
  created_at: string; shipped_at: string | null; delivered_at: string | null; is_return: boolean;
}
export interface InsightService {
  name: string; origin: string; execution_status: string | null;
  approval_status: string | null; not_done_reason: string | null;
}
export interface InsightChecklist { stage: string | null; status: string; corrections: number }
export interface ServiceOrderInsightsFacts {
  created_at: string; status: string; scheduled_start: string | null; duration_minutes: number | null;
  events: InsightEvent[]; services: InsightService[]; checklists: InsightChecklist[]; shipments: InsightShipment[];
}

/** Trecho da linha do tempo (situação → duração). */
export interface StatusSegment { status: string; start: string; end: string | null; seconds: number }

const ms = (iso: string) => new Date(iso).getTime();
const diff = (a: string | null | undefined, b: string | null | undefined) =>
  a && b ? Math.max(0, Math.round((ms(b) - ms(a)) / 1000)) : null;

/** Primeiro evento de um tipo (ou de uma situação nova). */
const firstAt = (events: InsightEvent[], type: string, newValue?: string) =>
  events.find(e => e.type === type && (newValue === undefined || e.new === newValue))?.at ?? null;

/** Trechos por situação: intervalo entre eventos `status` consecutivos; a 1ª começa na abertura. */
export function statusSegments(f: ServiceOrderInsightsFacts, now: string = new Date().toISOString()): StatusSegment[] {
  const changes = f.events.filter(e => e.type === 'status' && e.new && e.new !== e.old);
  const segments: StatusSegment[] = [];
  let current = changes[0]?.old ?? 'open';
  let start = f.created_at;
  for (const c of changes) {
    segments.push({ status: current, start, end: c.at, seconds: diff(start, c.at) ?? 0 });
    current = c.new as string; start = c.at;
  }
  const finished = ['completed', 'cancelled'].includes(current);
  segments.push({ status: current, start, end: finished ? start : null, seconds: finished ? 0 : diff(start, now) ?? 0 });
  return segments.filter(s => s.seconds > 0 || s.end === null);
}

/** Pausas: soma dos pares timer_paused → timer_resumed (pausa aberta conta até agora). */
export function pausedSeconds(events: InsightEvent[], now: string = new Date().toISOString()): number {
  let total = 0; let openAt: string | null = null;
  for (const e of events) {
    if (e.type === 'timer_paused' && !openAt) openAt = e.at;
    else if (e.type === 'timer_resumed' && openAt) { total += diff(openAt, e.at) ?? 0; openAt = null; }
  }
  if (openAt) total += diff(openAt, now) ?? 0;
  return total;
}

export interface ServiceOrderInsights {
  totalSeconds: number | null;        // abertura → conclusão
  timeToArrivalSeconds: number | null; // abertura → check-in
  travelSeconds: number | null;        // início deslocamento → check-in
  executionSeconds: number | null;     // check-in → envio p/ revisão (menos pausas)
  estimatedSeconds: number | null;
  delaySeconds: number | null;         // check-in − início agendado (negativo = adiantado)
  reworkCount: number;
  servicesDone: number; servicesTotal: number; fieldAdded: number;
  notDone: { name: string; reason: string | null }[];
  checklistsApproved: number; checklistsTotal: number; checklistsWithCorrection: number;
  freightTotal: number | null; freightMissing: number; shipmentsCount: number;
  segments: StatusSegment[];
}

export function computeServiceOrderInsights(f: ServiceOrderInsightsFacts, now: string = new Date().toISOString()): ServiceOrderInsights {
  const ev = f.events;
  const checkIn = firstAt(ev, 'provider_check_in');
  const travel = firstAt(ev, 'provider_travel_started');
  const completed = [...ev].reverse().find(e => e.type === 'status' && e.new === 'completed')?.at ?? null;
  const review = firstAt(ev, 'status', 'awaiting_review');
  const execRaw = diff(checkIn, review);
  const answered = f.services.filter(s => s.origin !== 'field' || s.approval_status === 'approved');
  const freights = f.shipments.map(s => s.freight_cost).filter((v): v is number => v !== null && v !== undefined);
  return {
    totalSeconds: diff(f.created_at, completed),
    timeToArrivalSeconds: diff(f.created_at, checkIn),
    travelSeconds: diff(travel, checkIn),
    executionSeconds: execRaw === null ? null : Math.max(0, execRaw - pausedSeconds(ev, review ?? now)),
    estimatedSeconds: f.duration_minutes ? f.duration_minutes * 60 : null,
    delaySeconds: checkIn && f.scheduled_start ? Math.round((ms(checkIn) - ms(f.scheduled_start)) / 1000) : null,
    reworkCount: ev.filter(e => e.type === 'status' && e.new === 'in_correction').length,
    servicesDone: answered.filter(s => s.execution_status === 'done').length,
    servicesTotal: answered.length,
    fieldAdded: f.services.filter(s => s.origin === 'field').length,
    notDone: f.services.filter(s => s.execution_status === 'not_done').map(s => ({ name: s.name, reason: s.not_done_reason })),
    checklistsApproved: f.checklists.filter(c => c.status === 'approved').length,
    checklistsTotal: f.checklists.filter(c => c.status !== 'cancelled').length,
    checklistsWithCorrection: f.checklists.filter(c => c.corrections > 0).length,
    freightTotal: freights.length ? freights.reduce((a, b) => a + Number(b), 0) : null,
    freightMissing: f.shipments.length - freights.length,
    shipmentsCount: f.shipments.length,
    segments: statusSegments(f, now),
  };
}

/** Formata segundos como "2 d 3 h", "1 h 05 min" ou "12 min"; null → "Não registrado". */
export function formatInsightDuration(seconds: number | null): string {
  if (seconds === null) return 'Não registrado';
  const m = Math.round(Math.abs(seconds) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ${String(m % 60).padStart(2, '0')} min`;
  return `${Math.floor(h / 24)} d ${h % 24} h`;
}

/** Pontualidade: tolerância de 5 min conta como no prazo. */
export function punctualityLabel(delaySeconds: number | null): string {
  if (delaySeconds === null) return 'Não registrado';
  if (delaySeconds <= 300) return 'No prazo';
  return `Atraso de ${formatInsightDuration(delaySeconds)}`;
}

// ============================================================================
// Linha do tempo (Gantt) — RELEASE: 2026-10-18 v1.1.0
// ============================================================================

export type StatusGroup = 'logistics' | 'unscheduled' | 'scheduling' | 'travel' | 'execution' | 'review' | 'done';

export const STATUS_GROUP_LABEL: Record<StatusGroup, string> = {
  logistics: 'Logística', unscheduled: 'Sem agendamento', scheduling: 'Agendamento', travel: 'Deslocamento', execution: 'Execução', review: 'Revisão', done: 'Concluída',
};

/** Grupo visual de cada situação da O.S. */
export function statusGroup(status: string): StatusGroup {
  if (['awaiting_shipment', 'shipment_in_transit', 'shipment_delivered'].includes(status)) return 'logistics';
  if (status === 'travel') return 'travel'; // v1.2.0: trecho sintético do deslocamento
  if (['in_progress'].includes(status)) return 'execution';
  if (['awaiting_review', 'in_correction'].includes(status)) return 'review';
  if (['completed', 'cancelled'].includes(status)) return 'done';
  if (['open', 'to_reschedule'].includes(status)) return 'unscheduled'; // v1.3.0
  return 'scheduling'; // scheduled
}

export interface GanttBar extends StatusSegment { left: number; width: number; group: StatusGroup }

/** Posição/largura percentual de cada trecho no eixo [start, end]; largura mínima para etapas curtas. */
export function ganttBars(segments: StatusSegment[], start: string, end: string, minWidth = 1.5): GanttBar[] {
  const s = ms(start); const span = Math.max(1, ms(end) - s);
  return segments.map(seg => {
    const left = Math.min(100, Math.max(0, ((ms(seg.start) - s) / span) * 100));
    const width = Math.max(minWidth, ((seg.seconds * 1000) / span) * 100);
    return { ...seg, left: Math.min(left, 100 - minWidth), width: Math.min(width, 100 - Math.min(left, 100 - minWidth)), group: statusGroup(seg.status) };
  });
}

/** Marcas do eixo: `count` pontos igualmente espaçados (inclui início e fim). */
export function ganttTicks(start: string, end: string, count = 5): { at: string; left: number }[] {
  const s = ms(start); const e = ms(end);
  return Array.from({ length: count }, (_, i) => ({ at: new Date(s + ((e - s) * i) / (count - 1)).toISOString(), left: (i / (count - 1)) * 100 }));
}

/** Posição percentual de um instante no eixo; fora do intervalo → null. */
export function ganttPosition(at: string | null, start: string, end: string): number | null {
  if (!at) return null;
  const p = ((ms(at) - ms(start)) / Math.max(1, ms(end) - ms(start))) * 100;
  return p < 0 || p > 100 ? null : p;
}

/** Soma de segundos por grupo (para o resumo e a barra empilhada). */
export function groupTotals(segments: StatusSegment[]): Record<StatusGroup, number> {
  const t: Record<StatusGroup, number> = { logistics: 0, unscheduled: 0, scheduling: 0, travel: 0, execution: 0, review: 0, done: 0 };
  for (const s of segments) t[statusGroup(s.status)] += s.seconds;
  return t;
}

/**
 * v1.4.0: totais dos ciclos do resumo. "Sem agendamento" vai da criação até a O.S. ficar
 * Agendada, então absorve a logística (remessa) que ocorre nesse intervalo — o tempo não é
 * contado duas vezes. A logística fica disponível à parte como detalhe (`logisticsWithinUnscheduled`).
 */
export function cycleTotals(segments: StatusSegment[]): Record<StatusGroup, number> & { logisticsWithinUnscheduled: number } {
  const t = groupTotals(segments);
  const logistics = t.logistics;
  return { ...t, unscheduled: t.unscheduled + logistics, logistics: 0, logisticsWithinUnscheduled: logistics };
}

/**
 * v1.2.0: recorta o intervalo [início do deslocamento, check-in] dos trechos existentes e insere
 * um trecho `travel`. Sem check-in o deslocamento segue aberto até agora (end null).
 * Sem início do deslocamento devolve os trechos intactos. A soma total não muda.
 */
export function withTravelSegment(
  segments: StatusSegment[], travelAt: string | null, checkInAt: string | null, now: string = new Date().toISOString(),
): StatusSegment[] {
  if (!travelAt || !segments.length) return segments;
  const t0 = ms(travelAt);
  const t1 = ms(checkInAt ?? now);
  if (t1 <= t0) return segments;
  const out: StatusSegment[] = [];
  let inserted = false;
  const piece = (status: string, a: number, b: number, open: boolean): StatusSegment => ({
    status, start: new Date(a).toISOString(), end: open ? null : new Date(b).toISOString(), seconds: Math.max(0, Math.round((b - a) / 1000)),
  });
  for (const seg of segments) {
    const a = ms(seg.start); const b = seg.end === null ? ms(now) : ms(seg.end);
    const open = seg.end === null;
    if (b <= t0 || a >= t1) { // sem sobreposição
      if (!inserted && a >= t1) { out.push(piece('travel', t0, t1, !checkInAt)); inserted = true; }
      out.push(seg); continue;
    }
    if (a < t0) out.push(piece(seg.status, a, t0, false));
    if (!inserted) { out.push(piece('travel', t0, t1, !checkInAt)); inserted = true; }
    if (b > t1) out.push(piece(seg.status, t1, b, open));
  }
  if (!inserted) out.push(piece('travel', t0, t1, !checkInAt));
  return out.filter(s => s.seconds > 0 || s.end === null);
}
